import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { HttpRestService } from '@core/services/http-rest';
import { normalizeToPrecision } from '@core';
import { AMOUNT_INPUT_CONFIG } from '@core/config/number-precision.config';
import {
  CreateBuyItemRequest,
  GetBundleExchangeRatesRequest,
  GetExchangeRatesRequest,
} from '../domain/giftcard.request';
import {
  BundleCalculationDataResponse,
  CreateBuyItemByWalletResponse,
  CreateBuyItemResponse,
  GetBundleDetailsResponse,
  GetCalculationDataResponse,
  GiftCardFinancialInfoResponse,
  ServiceProviderDetailsResponse,
} from '../domain/giftcard.response';
import { GIFTCARD_ENDPOINTS } from '../domain/giftcard.endpoints';

@Injectable({ providedIn: 'root' })
export class GiftcardService {
  private readonly _http = inject(HttpRestService);
  private readonly _amountConfig = inject(AMOUNT_INPUT_CONFIG);

  // ─── Helpers ──────────────────────────────────────────────────────────────

  /** Normalises a buy-rate to the target currency's precision (or config default). */
  private _normaliseBuyRate(
    buyRate: string | null | undefined,
    toPrecision: number | undefined,
  ): string {
    return normalizeToPrecision(
      buyRate,
      toPrecision ?? this._amountConfig.maxDecimalDigits,
    );
  }

  // ─── API calls ────────────────────────────────────────────────────────────

  getServiceProviderDetails(
    id: string,
  ): Observable<ServiceProviderDetailsResponse> {
    return this._http
      .get<ServiceProviderDetailsResponse>(
        GIFTCARD_ENDPOINTS.serviceProviderDetails(id),
      )
      .pipe(
        map((response) => ({
          ...response,
          manualServiceProviderItems:
            response.manualServiceProviderItems?.map((item) => {
              const precision =
                item.currency?.decimalPrecision ??
                this._amountConfig.maxDecimalDigits;
              return {
                ...item,
                amounts:
                  item.amounts?.map((a) => ({
                    ...a,
                    amount: normalizeToPrecision(a.amount, precision),
                  })) ?? [],
              };
            }) ?? [],
          automaticServiceProviderItems:
            response.automaticServiceProviderItems?.map((item) => {
              const precision =
                item.currency?.decimalPrecision ??
                this._amountConfig.maxDecimalDigits;
              return {
                ...item,
                minimumValue: normalizeToPrecision(
                  item.minimumValue,
                  precision,
                ),
                maximumValue: normalizeToPrecision(
                  item.maximumValue,
                  precision,
                ),
              };
            }) ?? [],
        })),
      );
  }

  getBundleDetails(id: string): Observable<GetBundleDetailsResponse> {
    return this._http.get<GetBundleDetailsResponse>(
      GIFTCARD_ENDPOINTS.bundleDetails(id),
    );
  }

  getFinancialInfo(
    serviceProviderItemId: string,
    amount?: string,
  ): Observable<GiftCardFinancialInfoResponse> {
    const params: Record<string, string> = {};
    if (amount !== undefined) {
      params['Amount'] = amount;
    }
    return this._http.get<GiftCardFinancialInfoResponse>(
      GIFTCARD_ENDPOINTS.financialInfo(serviceProviderItemId),
      { params },
    );
  }

  getExchangeRates(
    request: GetExchangeRatesRequest,
  ): Observable<GetCalculationDataResponse> {
    return this._http
      .get<GetCalculationDataResponse>(GIFTCARD_ENDPOINTS.exchangeRates, {
        params: {
          requestType: request.requestType,
          serviceProviderItemId: request.serviceProviderItemId,
          paymentMethodProviderId: request.paymentMethodProviderId,
          amount: request.amount,
        },
      })
      .pipe(
        map((response) => ({
          ...response,
          exchangeRate: response.exchangeRate
            ? {
                ...response.exchangeRate,
                buyRate: this._normaliseBuyRate(
                  response.exchangeRate.buyRate,
                  response.exchangeRate.toCurrency?.decimalPrecision,
                ),
              }
            : null,
        })),
      );
  }

  getBundleExchangeRates(
    request: GetBundleExchangeRatesRequest,
  ): Observable<BundleCalculationDataResponse> {
    return this._http
      .get<BundleCalculationDataResponse>(
        GIFTCARD_ENDPOINTS.bundleExchangeRates,
        {
          params: {
            requestType: request.requestType,
            bundleId: request.bundleId,
            paymentMethodProviderId: request.paymentMethodProviderId,
          },
        },
      )
      .pipe(
        map((response) => ({
          ...response,
          calculationDataItems:
            response.calculationDataItems?.map((item) => ({
              ...item,
              exchangeRate: item.exchangeRate
                ? {
                    ...item.exchangeRate,
                    buyRate: this._normaliseBuyRate(
                      item.exchangeRate.buyRate,
                      item.exchangeRate.toCurrency?.decimalPrecision,
                    ),
                  }
                : null,
            })) ?? [],
        })),
      );
  }

  submitBuyItem(
    serviceProviderItemId: string,
    request: CreateBuyItemRequest,
  ): Observable<CreateBuyItemResponse> {
    return this._http.post<CreateBuyItemResponse, CreateBuyItemRequest>(
      GIFTCARD_ENDPOINTS.buyItem(serviceProviderItemId),
      request,
    );
  }
  submitBundle(
    bundleId: string,
    request: CreateBuyItemRequest,
  ): Observable<CreateBuyItemResponse> {
    return this._http.post<CreateBuyItemResponse, CreateBuyItemRequest>(
      GIFTCARD_ENDPOINTS.buyBundle(bundleId),
      request,
    );
  }
  submitBuyItemByWallet(
    serviceProviderItemId: string,
    request: CreateBuyItemRequest,
  ): Observable<CreateBuyItemByWalletResponse> {
    return this._http.post<CreateBuyItemByWalletResponse, CreateBuyItemRequest>(
      GIFTCARD_ENDPOINTS.buyItemByWallet(serviceProviderItemId),
      request,
    );
  }
  submitBundleByWallet(
    bundleId: string,
    request: CreateBuyItemRequest,
  ): Observable<CreateBuyItemByWalletResponse> {
    return this._http.post<CreateBuyItemByWalletResponse, CreateBuyItemRequest>(
      GIFTCARD_ENDPOINTS.buyBundleByWallet(bundleId),
      request,
    );
  }
}
